// User & Auth Types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  avatar?: string;
  storeId?: string;
  ownedStores?: Store[];
  stores?: Store[];
  memberships?: any[];
  store?: Store;
  account?: any;
  membershipRole?: string;
  organisationName?: string;
  plan?: 'BASIC' | 'PRO' | 'MAX' | 'ENTERPRISE';
  emailVerified: boolean;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  MERCHANT = 'MERCHANT',
  OWNER = 'OWNER',
  STAFF = 'STAFF',
  CO_OWNER = 'CO_OWNER',
  AFFILIATE = 'AFFILIATE',
  CUSTOMER = 'CUSTOMER',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

// Store Types
export interface Store {
  id: string;
  name: string;
  subdomain: string;
  paymentProviders?: any[];
  paymentChannels?: any[];
  description?: string;
  logo?: string;
  banner?: string;
  email: string;
  phone: string;
  address?: Address;
  settings: StoreSettings;
  ownerId: string;
  status: StoreStatus;
  whatsappConfig?: {
    displayPhoneNumber?: string | null;
    bubbleMessage?: string | null;
    bubbleEnabled?: boolean;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export enum StoreStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
}

export interface StoreSettings {
  currency: string;
  timezone: string;
  language: string;
  taxRate: number;
  minOrderAmount: number;
  freeShippingThreshold?: number;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  paymentMethods: {
    mpesa: boolean;
    card: boolean;
    cash: boolean;
  };
  featuredCategoryId1?: string;
  featuredCategoryId2?: string;
  featuredCategoryId3?: string;
  featuredCategory1?: string;
  featuredCategory2?: string;
  featuredCategory3?: string;
  [key: string]: any;
}

// Product Types
export interface Product {
  id: string;
  storeId: string;
  title: string; // Database uses 'title' field
  name?: string; // Alias for backwards compatibility
  handle: string;
  slug: string;
  description?: string;
  shortDescription?: string;
  sku: string;
  price: number;
  compareAtPrice?: number;
  cost?: number;
  images: ProductImage[];
  category?: ProductCategory;
  categories?: ProductCategory[];
  categoryId?: string;
  variants: ProductVariant[];
  options: ProductOption[];
  tags: string[];
  status: ProductStatus;
  featured: boolean;
  inventory: InventoryItem;
  seo?: SEOMetadata;
  createdAt: string;
  updatedAt: string;
  activeDiscount?: {
    id: string;
    name: string;
    type: string;
    value: number;
    code?: string | null;
  };
  discountedPrice?: number;
}

export enum ProductStatus {
  ACTIVE = 'ACTIVE',
  DRAFT = 'DRAFT',
  ARCHIVED = 'ARCHIVED',
}

export interface BulkProductPayload {
  title: string;
  handle: string;
  shortDescription?: string;
  description?: string;
  sku?: string;
  price: number;
  compareAtPrice?: number;
  costPerItem?: number;
  categoryId?: string;
  categories: string[];
  status?: 'ACTIVE' | 'DRAFT';
  trackInventory: boolean;
  initialQuantity: number;
  lowStockThreshold?: number;
  featured: boolean;
  tags: string[];
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  affiliateEnabled: boolean;
  commissionType?: 'PERCENTAGE' | 'FIXED';
  commissionValue?: number;
  image_url?: string;
}

export interface ProductImage {
  id: string;
  url: string;
  alt?: string;
  position: number;
}

export interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  image?: string;
  parentId?: string;
  position: number;
}

export interface ProductVariant {
  id: string;
  productId: string;
  name: string;
  sku: string;
  price: number;
  compareAtPrice?: number;
  options: Record<string, string>;
  inventory: InventoryItem;
  image?: ProductImage;
}

export interface ProductOption {
  name: string;
  values: string[];
}

export interface SEOMetadata {
  title?: string;
  description?: string;
  keywords?: string[];
}

// Inventory Types
export interface InventoryItem {
  id: string;
  productId: string;
  variantId?: string;
  quantity: number;
  reserved: number;
  available: number;
  lowStockThreshold?: number;
  trackQuantity: boolean;
  allowBackorder: boolean;
}

export interface InventoryAdjustment {
  id: string;
  inventoryId: string;
  quantity: number;
  type: AdjustmentType;
  reason?: string;
  createdBy: string;
  createdAt: string;
}

export enum AdjustmentType {
  RESTOCK = 'RESTOCK',
  DAMAGE = 'DAMAGE',
  LOSS = 'LOSS',
  RETURN = 'RETURN',
  CORRECTION = 'CORRECTION',
}

// Order Types
export interface Order {
  id: string;
  orderNumber: string;
  storeId: string;
  customerId?: string;
  customer?: Customer;
  email?: string;
  phone?: string;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  total: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  fulfillmentStatus: FulfillmentStatus;
  shippingAddress?: Address;
  billingAddress?: Address;
  notes?: string;
  trackingNumber?: string;
  paymentMethod: PaymentMethod | string;
  salesChannel?: string;
  source?: string;
  paymentDetails?: {
    provider?: string;
    confirmationCode?: string | null;
    phone?: string | null;
    amount?: number;
    status?: string;
    confirmedAt?: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

export enum OrderStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  PROCESSING = 'PROCESSING',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
}

export enum FulfillmentStatus {
  UNFULFILLED = 'UNFULFILLED',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  FULFILLED = 'FULFILLED',
}

export interface OrderItem {
  id: string;
  productId: string;
  variantId?: string;
  name: string;
  sku: string;
  quantity: number;
  price: number;
  total: number;
  image?: string;
}

// Customer Types
export interface Customer {
  id: string;
  name?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  addresses: Address[];
  defaultAddressId?: string;
  totalOrders: number;
  totalSpent: number;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Address {
  id?: string;
  firstName: string;
  lastName: string;
  company?: string;
  address1: string;
  address2?: string;
  line1?: string;
  line2?: string;
  city: string;
  county?: string;
  province?: string;
  postalCode?: string;
  zip?: string;
  country: string;
  phone?: string;
  isDefault?: boolean;
  shippingMethod?: {
    providerId: string;
    label: string;
    amount: number;
    currency: string;
    townId?: string;
    pickupName?: string;
    pickupAddress?: string;
    pickupPhone?: string;
    estimatedDays?: number;
  };
}

// Cart Types
export interface Cart {
  id: string;
  customerId?: string;
  items: CartItem[];
  subtotal: number;
  tax: number;
  shipping: number;
  discount: number;
  total: number;
  couponCode?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CartItem {
  id: string;
  productId: string;
  variantId?: string;
  name: string;
  sku: string;
  price: number;
  quantity: number;
  total: number;
  image?: string;
  options?: Record<string, string>;
}

// Payment Types
export interface Payment {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  method: PaymentMethod;
  status: PaymentStatus;
  transactionId?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export enum PaymentMethod {
  MPESA = 'MPESA',
  CARD = 'CARD',
  CASH = 'CASH',
}

export interface MpesaPaymentRequest {
  phoneNumber: string;
  amount: number;
  orderId: string;
}

export interface MpesaPaymentResponse {
  checkoutRequestId: string;
  merchantRequestId: string;
  responseCode: string;
  responseDescription: string;
  customerMessage: string;
}

// Analytics Types
export interface DashboardStats {
  totalRevenue: number;
  revenueChange: number;
  totalOrders: number;
  ordersChange: number;
  totalCustomers: number;
  customersChange: number;
  averageOrderValue: number;
  aovChange: number;
}

export interface SalesData {
  date: string;
  revenue: number;
  orders: number;
  customers: number;
}

export interface TopProduct {
  id: string;
  name: string;
  image?: string;
  revenue: number;
  orders: number;
  quantity: number;
}

export interface RevenueByCategory {
  category: string;
  revenue: number;
  percentage: number;
}

// Webhook Types
export interface Webhook {
  id: string;
  url: string;
  events: WebhookEvent[];
  secret: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum WebhookEvent {
  ORDER_CREATED = 'order.created',
  ORDER_UPDATED = 'order.updated',
  ORDER_CANCELLED = 'order.cancelled',
  PAYMENT_COMPLETED = 'payment.completed',
  PAYMENT_FAILED = 'payment.failed',
  PRODUCT_CREATED = 'product.created',
  PRODUCT_UPDATED = 'product.updated',
  INVENTORY_UPDATED = 'inventory.updated',
}

// Pagination Types
export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

// Filter Types
export interface ProductFilters extends PaginationParams {
  search?: string;
  categoryId?: string;
  status?: ProductStatus;
  minPrice?: number;
  maxPrice?: number;
  tags?: string[];
  featured?: boolean;
}

export interface OrderFilters extends PaginationParams {
  search?: string;
  status?: OrderStatus;
  paymentStatus?: PaymentStatus;
  fulfillmentStatus?: FulfillmentStatus;
  customerId?: string;
  startDate?: string;
  endDate?: string;
}

export interface CustomerFilters extends PaginationParams {
  search?: string;
  tags?: string[];
  minTotalSpent?: number;
  maxTotalSpent?: number;
}
export * from './locations';
export * from './checkout-link';
